'use client';

import { useState, useMemo, useCallback } from 'react';
import {
    format, startOfMonth, endOfMonth, getDay,
    isToday, isSameMonth, isSameDay,
    addMonths, subMonths, startOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarEvent } from '@/lib/types';
import { EventForm } from './EventForm';
import { EventDetails } from './EventDetails';
import { DayEventsDialog } from './DayEventsDialog';
import { Loader2, ChevronLeft, ChevronRight, Plus, Church, Cross } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { getCalendarioHermandadColors } from '@/lib/actions';
import { useEffect } from 'react';

/** Datos visuales de un evento una vez resuelta su hermandad. */
export interface EventStyle {
    color: string;
    escudoUrl?: string;
    hermandadNombre?: string;
}

const WEEK_DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const DEFAULT_EVENT_COLOR = '#b4841c';

/** Convierte el color del evento (#rrggbb) en rgba para teñir el fondo del día. */
export function hexToRgba(hex: string, alpha: number): string {
    const h = (hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return `rgba(180, 132, 28, ${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildCalendarGrid(date: Date): Date[] {
    const start = startOfMonth(date);
    // Grid starts on Monday of the week containing the 1st
    const gridStart = startOfWeek(start, { weekStartsOn: 1 });
    const days: Date[] = [];
    const cursor = new Date(gridStart);
    for (let i = 0; i < 42; i++) {
        days.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return days;
}

interface CalendarProps {
    events: CalendarEvent[];
    isLoading: boolean;
    isEditable: boolean;
    onCreateEvent?: (event: Omit<CalendarEvent, 'id'>) => Promise<any>;
    onUpdateEvent?: (eventId: string, event: Partial<CalendarEvent>) => Promise<any>;
    onDeleteEvent?: (eventId: string) => Promise<any>;
}

export function Calendar({ events, isLoading, isEditable, onCreateEvent, onUpdateEvent, onDeleteEvent }: CalendarProps) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | undefined>(undefined);
    const [eventToEdit, setEventToEdit] = useState<Partial<CalendarEvent> | undefined>(undefined);
    const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);
    const [hermandadColors, setHermandadColors] = useState<Record<string, string>>({});

    const { jartinetData } = useAuth();

    useEffect(() => { getCalendarioHermandadColors().then(setHermandadColors); }, []);

    const hermandadesById = useMemo(() => {
        const map = new Map<string, { nombre: string; escudoUrl: string; colorPrimario: string }>();
        (jartinetData?.hermandades || []).forEach(h => {
            map.set(h.id, { nombre: h.nombre, escudoUrl: h.escudoUrl, colorPrimario: h.colorPrimario });
        });
        return map;
    }, [jartinetData?.hermandades]);

    /**
     * Color e icono de un evento: si tiene hermandad, manda el color configurado
     * para ella (y su escudo sustituye al icono de tipo). Si no se ha configurado
     * ninguno, por defecto se usa el `colorPrimario` de la propia hermandad.
     */
    const styleOf = useCallback((event: CalendarEvent): EventStyle => {
        const h = event.hermandadId ? hermandadesById.get(event.hermandadId) : undefined;
        const hColor = event.hermandadId ? hermandadColors[event.hermandadId] : undefined;
        return {
            color: hColor || h?.colorPrimario || event.color || DEFAULT_EVENT_COLOR,
            escudoUrl: h?.escudoUrl,
            hermandadNombre: h?.nombre,
        };
    }, [hermandadesById, hermandadColors]);

    const calendarDays = useMemo(() => buildCalendarGrid(currentDate), [currentDate]);

    // Map each calendar date key → events that span that day
    const eventsByDay = useMemo(() => {
        const map = new Map<string, CalendarEvent[]>();
        events.forEach(event => {
            const start = new Date(event.start);
            const end = new Date(event.end);
            const cursor = new Date(start);
            cursor.setHours(0, 0, 0, 0);
            const endDay = new Date(end);
            endDay.setHours(0, 0, 0, 0);
            while (cursor <= endDay) {
                const key = format(cursor, 'yyyy-MM-dd');
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(event);
                cursor.setDate(cursor.getDate() + 1);
            }
        });
        return map;
    }, [events]);

    /**
     * "Carriles" de cultos por semana. Los cultos de una misma hermandad ocupan
     * el mismo carril en todos los días de esa semana, de modo que si la hermandad
     * tiene culto en días seguidos, ambos se pintan a la misma altura. Los días sin
     * culto en un carril dejan un hueco para mantener la alineación.
     *
     * Nota: la alineación es POR SEMANA (fila de la rejilla). Dos días consecutivos
     * a caballo entre dos filas (domingo → lunes) no pueden alinearse porque están
     * en filas distintas de la rejilla.
     */
    const cultoLanesByWeek = useMemo(() => {
        const weeks: Map<string, number>[] = [];
        for (let w = 0; w < 6; w++) {
            const lanes = new Map<string, number>();
            for (let d = 0; d < 7; d++) {
                const day = calendarDays[w * 7 + d];
                if (!day) continue;
                const dayEvents = eventsByDay.get(format(day, 'yyyy-MM-dd')) || [];
                dayEvents
                    .filter(e => e.tipo === 'cultos' && e.hermandadId)
                    .forEach(e => {
                        if (!lanes.has(e.hermandadId!)) lanes.set(e.hermandadId!, lanes.size);
                    });
            }
            weeks.push(lanes);
        }
        return weeks;
    }, [calendarDays, eventsByDay]);

    const prevMonth = useCallback(() => setCurrentDate(d => subMonths(d, 1)), []);
    const nextMonth = useCallback(() => setCurrentDate(d => addMonths(d, 1)), []);
    const goToday   = useCallback(() => setCurrentDate(new Date()), []);

    // Al pulsar un día: si tiene eventos, se abre la tarjeta con todos ellos
    // detallados. Si está vacío y el calendario es editable, se crea uno nuevo.
    const handleDayClick = useCallback((day: Date, dayEvents: CalendarEvent[]) => {
        if (dayEvents.length > 0) { setSelectedDay(day); return; }
        if (isEditable) setEventToEdit({ start: day, end: day, allDay: true });
    }, [isEditable]);

    const handleEventClick = useCallback((event: CalendarEvent, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEditable) setEventToEdit(event);
        else setSelectedEvent(event);
    }, [isEditable]);

    const handleFormSubmit = async (formData: Omit<CalendarEvent, 'id'> | CalendarEvent) => {
        let result;
        if ('id' in formData && formData.id) {
            result = await onUpdateEvent?.(formData.id, formData);
        } else {
            result = await onCreateEvent?.(formData);
        }
        if (result?.success) setEventToEdit(undefined);
        return result;
    };

    const handleDelete = async (eventId: string) => {
        const result = await onDeleteEvent?.(eventId);
        if (result?.success) setEventToEdit(undefined);
    };

    return (
        <div className="flex flex-col rounded-2xl border border-white/8 bg-card/40 backdrop-blur-sm overflow-hidden">

            {/* ── Month header ── */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <h2 className="font-headline text-lg font-bold capitalize">
                        {format(currentDate, 'MMMM yyyy', { locale: es })}
                    </h2>
                    {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
                <div className="flex items-center gap-2">
                    {isEditable && (
                        <Button
                            size="sm"
                            className="h-8 px-3 text-xs rounded-xl gap-1"
                            onClick={() => setEventToEdit({ start: new Date(), end: new Date(), allDay: false })}
                        >
                            <Plus className="h-3.5 w-3.5" /> Evento
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={goToday} className="h-8 px-3 text-xs rounded-xl border-white/15">
                        Hoy
                    </Button>
                    <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8 rounded-xl">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8 rounded-xl">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* ── Day-of-week header ── */}
            <div className="grid grid-cols-7 border-b-4 border-white/25 flex-shrink-0">
                {WEEK_DAYS.map(d => (
                    <div key={d} className="py-2 text-center text-[0.65rem] font-semibold text-muted-foreground/60 uppercase tracking-widest">
                        {d}
                    </div>
                ))}
            </div>

            {/* ── Calendar grid (42 cells = 6 rows × 7) ──
                Sin scroll interno: cada fila crece hasta la celda más alta de esa
                semana, así que todo el contenido del día se ve entero. La página es
                la que scrollea si el mes no cabe en el alto de la ventana. */}
            <div className="grid grid-cols-7">
                {calendarDays.map((day, idx) => {
                    const key = format(day, 'yyyy-MM-dd');
                    const dayEvents = eventsByDay.get(key) || [];
                    const inMonth  = isSameMonth(day, currentDate);
                    const isWeekend = [0, 6].includes(day.getDay());
                    const todayDay  = isToday(day);
                    // Las procesiones se muestran junto al número; los cultos, en su propia
                    // sección al pie. El resto son los eventos "normales" del día.
                    const procesiones = dayEvents.filter(e => e.tipo === 'procesion');
                    const cultos = dayEvents.filter(e => e.tipo === 'cultos');
                    const normales = dayEvents.filter(e => e.tipo !== 'procesion' && e.tipo !== 'cultos');
                    // El día se tiñe con el color de la procesión (la primera si hay varias).
                    const procStyle = procesiones[0] ? styleOf(procesiones[0]) : null;
                    const procColor = procStyle?.color || DEFAULT_EVENT_COLOR;

                    // Reparte los cultos del día en los carriles de su semana para que
                    // los de la misma hermandad queden alineados entre días seguidos.
                    const lanes = cultoLanesByWeek[Math.floor(idx / 7)] || new Map<string, number>();
                    const laneSlots: (CalendarEvent | null)[] = new Array(lanes.size).fill(null);
                    const sinCarril: CalendarEvent[] = [];
                    cultos.forEach(e => {
                        const lane = e.hermandadId ? lanes.get(e.hermandadId) : undefined;
                        if (lane !== undefined && laneSlots[lane] === null) laneSlots[lane] = e;
                        else sinCarril.push(e);
                    });
                    // Solo se pintan los carriles hasta el último con contenido ese día
                    // (los huecos intermedios sí, para no romper la alineación).
                    const lastUsedLane = laneSlots.reduce((last, e, i) => (e ? i : last), -1);
                    const visibleLanes = laneSlots.slice(0, lastUsedLane + 1);
                    const cultosExtra = sinCarril;

                    return (
                        <div
                            key={idx}
                            onClick={() => handleDayClick(day, dayEvents)}
                            className={cn(
                                // Borde inferior más marcado que el vertical → cada semana
                                // se lee como una banda separada de la siguiente.
                                "relative flex min-h-[112px] flex-col p-1.5 border-b-4 border-b-white/25 border-r border-r-white/15 transition-colors",
                                (isEditable || dayEvents.length > 0) && "cursor-pointer hover:bg-white/5",
                                !inMonth && "opacity-30",
                                isWeekend && inMonth && "bg-white/[0.015]",
                            )}
                            style={procesiones.length > 0 ? {
                                backgroundColor: hexToRgba(procColor, 0.22),
                                boxShadow: `inset 0 0 0 1px ${hexToRgba(procColor, 0.5)}`,
                            } : undefined}
                        >
                            {/* Cabecera: procesión a la izquierda, número del día a la derecha */}
                            <div className="flex shrink-0 items-center justify-between gap-1 mb-1">
                                {procesiones.length > 0 ? (
                                    <span
                                        title={procesiones.map(p => p.title).join(' · ')}
                                        className="flex min-w-0 flex-1 items-center gap-1 text-[0.72rem] font-bold leading-tight text-white"
                                    >
                                        {procStyle?.escudoUrl
                                            ? <img src={procStyle.escudoUrl} alt="" className="h-5 w-5 shrink-0 object-contain" />
                                            : <Church className="h-4 w-4 shrink-0" style={{ color: procColor }} />}
                                        <span className="truncate">{procesiones[0].title}</span>
                                        {procesiones.length > 1 && (
                                            <span className="shrink-0">+{procesiones.length - 1}</span>
                                        )}
                                    </span>
                                ) : <span className="flex-1" />}
                                <span className={cn(
                                    "shrink-0",
                                    "flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold transition-colors",
                                    todayDay
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : inMonth
                                            ? "text-foreground hover:bg-white/10"
                                            : "text-muted-foreground/70"
                                )}>
                                    {format(day, 'd')}
                                </span>
                            </div>

                            {/* Cuerpo: eventos normales y, debajo, la sección de cultos */}
                            <div className="flex-1 space-y-0.5">
                                {normales.slice(0, 3).map((event, i) => {
                                    const st = styleOf(event);
                                    return (
                                        <button
                                            key={`${event.id}-${i}`}
                                            onClick={(e) => handleEventClick(event, e)}
                                            title={st.hermandadNombre ? `${event.title} — ${st.hermandadNombre}` : event.title}
                                            className="w-full flex items-center gap-1 text-left px-1.5 py-[2px] rounded-md text-[0.6rem] font-medium leading-tight transition-all hover:brightness-110 hover:shadow-sm"
                                            style={{ backgroundColor: st.color, color: 'white' }}
                                        >
                                            {st.escudoUrl && (
                                                <img src={st.escudoUrl} alt="" className="h-3 w-3 shrink-0 object-contain" />
                                            )}
                                            <span className="truncate">{event.title}</span>
                                        </button>
                                    );
                                })}
                                {normales.length > 3 && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedDay(day); }}
                                        className="w-full text-left text-[0.58rem] text-muted-foreground pl-1.5 font-medium hover:text-foreground transition-colors"
                                    >
                                        +{normales.length - 3} más
                                    </button>
                                )}

                                {cultos.length > 0 && (
                                    <div className="pt-3">
                                        <p className="text-left text-[0.55rem] font-bold uppercase tracking-wider text-violet-300/90 leading-none">
                                            Cultos
                                        </p>
                                        <div className="my-1 h-px bg-violet-300/30" />
                                        <div className="space-y-0.5">
                                            {/* Carriles: los huecos mantienen alineados los
                                                cultos de la misma hermandad entre días. */}
                                            {visibleLanes.map((event, laneIdx) => {
                                                // Hueco de la MISMA altura que un chip de culto
                                                // (borde 2 + padding 4 + icono 16), para que los
                                                // carriles queden alineados entre días.
                                                if (!event) return <div key={`lane-${laneIdx}`} className="h-[22px]" />;
                                                const st = styleOf(event);
                                                return (
                                                    <button
                                                        key={`${event.id}-c${laneIdx}`}
                                                        onClick={(e) => handleEventClick(event, e)}
                                                        title={st.hermandadNombre ? `Cultos — ${event.title} (${st.hermandadNombre})` : `Cultos — ${event.title}`}
                                                        className="w-full flex items-center gap-1 text-left px-1.5 py-[2px] rounded-md border border-dashed text-[0.6rem] font-medium italic leading-tight text-white transition-all hover:brightness-125"
                                                        style={{
                                                            borderColor: st.color,
                                                            backgroundColor: hexToRgba(st.color, 0.6),
                                                        }}
                                                    >
                                                        {st.escudoUrl
                                                            ? <img src={st.escudoUrl} alt="" className="h-4 w-4 shrink-0 object-contain" />
                                                            : <Cross className="h-3.5 w-3.5 shrink-0" />}
                                                        <span className="truncate">{event.title}</span>
                                                    </button>
                                                );
                                            })}

                                            {/* Cultos sin hermandad (no ocupan carril) */}
                                            {cultosExtra.slice(0, Math.max(0, 3 - visibleLanes.filter(Boolean).length)).map((event, i) => {
                                                const st = styleOf(event);
                                                return (
                                                    <button
                                                        key={`${event.id}-x${i}`}
                                                        onClick={(e) => handleEventClick(event, e)}
                                                        title={`Cultos — ${event.title}`}
                                                        className="w-full flex items-center gap-1 text-left px-1.5 py-[2px] rounded-md border border-dashed text-[0.6rem] font-medium italic leading-tight text-white transition-all hover:brightness-125"
                                                        style={{
                                                            borderColor: st.color,
                                                            backgroundColor: hexToRgba(st.color, 0.6),
                                                        }}
                                                    >
                                                        {st.escudoUrl
                                                            ? <img src={st.escudoUrl} alt="" className="h-4 w-4 shrink-0 object-contain" />
                                                            : <Cross className="h-3.5 w-3.5 shrink-0" />}
                                                        <span className="truncate">{event.title}</span>
                                                    </button>
                                                );
                                            })}

                                            {cultosExtra.length > Math.max(0, 3 - visibleLanes.filter(Boolean).length) && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSelectedDay(day); }}
                                                    className="w-full text-left text-[0.58rem] text-muted-foreground pl-1.5 font-medium hover:text-foreground transition-colors"
                                                >
                                                    +{cultosExtra.length - Math.max(0, 3 - visibleLanes.filter(Boolean).length)} más
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Dialogs ── */}
            {selectedDay && (
                <DayEventsDialog
                    day={selectedDay}
                    events={eventsByDay.get(format(selectedDay, 'yyyy-MM-dd')) || []}
                    isOpen={!!selectedDay}
                    isEditable={isEditable}
                    styleOf={styleOf}
                    onClose={() => setSelectedDay(undefined)}
                    onSelectEvent={(event) => {
                        setSelectedDay(undefined);
                        if (isEditable) setEventToEdit(event);
                        else setSelectedEvent(event);
                    }}
                    onCreate={(day) => {
                        setSelectedDay(undefined);
                        setEventToEdit({ start: day, end: day, allDay: true });
                    }}
                />
            )}
            {isEditable && eventToEdit && (
                <EventForm
                    event={eventToEdit}
                    isOpen={!!eventToEdit}
                    onClose={() => setEventToEdit(undefined)}
                    onSubmit={handleFormSubmit}
                    onDelete={handleDelete}
                />
            )}
            {!isEditable && selectedEvent && (
                <EventDetails
                    event={selectedEvent}
                    isOpen={!!selectedEvent}
                    onClose={() => setSelectedEvent(undefined)}
                />
            )}
        </div>
    );
}
